/**
 * The account, over the wire.
 *
 * Until now everything a player owned lived in `localStorage`, keyed by their
 * uuid — which made it per *browser* rather than per *account*. A second device,
 * a cleared cache or a different profile was a new player with a new starter
 * ship. This is the other end of that: the record lives in Postgres behind the
 * ChaosTree token, and the browser holds only a copy.
 *
 * ── localStorage is a cache now, not the store ──────────────────────────────
 *
 * It is written after every successful load and save, and read for exactly one
 * purpose: showing something while the first request is in flight, and after a
 * reconnect. It is never a place writes accumulate. Connectivity is required —
 * a player who cannot reach the server cannot progress — so a local copy that
 * diverged would only ever be a second, wrong answer.
 */

import type { PlayerRecord } from '@interspacial/shared';

/**
 * nginx path-routes this to the economy API in production; Vite proxies it in
 * development. Same path either way, so nothing here knows which it is.
 */
const BASE = '/interspacial/api';

export class NotSignedInError extends Error {
  constructor() {
    super('Your ChaosTree session has expired');
    this.name = 'NotSignedInError';
  }
}

export class OfflineError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the game server');
    this.name = 'OfflineError';
    this.cause = cause;
  }
}

/**
 * Wait as long as the server asked, then let the caller try once more.
 *
 * The header is authoritative and the body repeats it, because a proxy is free
 * to drop headers it does not recognise and the number matters more than where
 * it came from. Bounded either way: a server asking for an hour is a server with
 * a bug, and obeying it would look identical to the game hanging.
 */
async function waitOutLimit(response: Response): Promise<void> {
  const header = Number(response.headers.get('Retry-After'));
  const said = (await response.json().catch(() => ({}))) as { retryAfter?: number };

  const asked = Number.isFinite(header) && header > 0 ? header : (said.retryAfter ?? 1);
  const seconds = Math.min(10, Math.max(1, asked));

  await new Promise((resume) => setTimeout(resume, seconds * 1000));
}

async function call(
  path: string,
  token: string,
  init?: RequestInit,
  /** Guards the one retry below. Not for callers. */
  retried = false,
): Promise<Response> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    });
  } catch (error) {
    // A network failure, not a rejection. Worth telling apart: one is "sign in
    // again" and the other is "the server is not there", and a player can only
    // act on one of them.
    throw new OfflineError(error);
  }

  if (response.status === 401) throw new NotSignedInError();

  /*
   * Rate limited. Safe to ask again — see the longer note in `runAction`.
   *
   * This path carries the autosave, which is the one worth retrying hardest: a
   * refused save is not a refused click, it is hull damage and tutorial progress
   * that quietly never reached the server. Without this it would surface as
   * "offline" and the write would wait for whatever moved the state next.
   */
  if (response.status === 429 && !retried) {
    await waitOutLimit(response);
    return call(path, token, init, true);
  }

  if (!response.ok) throw new Error(`${path} failed: ${response.status}`);

  return response;
}

/**
 * Everything this account owns.
 *
 * Creates it server-side if this is the first time — there is no separate sign
 * up, so a ChaosTree user who has never played is simply one with no row, and
 * this is where they get a ship.
 */
/** What coming back is worth, if anything is waiting. */
export interface PendingRewards {
  daily: number;
  weekly: number;
}

export async function fetchAccount(
  token: string,
): Promise<{ record: PlayerRecord; pending: PendingRewards }> {
  const response = await call('/state', token);
  const body = (await response.json()) as { state: PlayerRecord; pending?: PendingRewards };

  /*
   * Absent means nothing waiting, not an error.
   *
   * The field is a sibling of the record rather than part of it — the record is
   * what is stored and this is a fact about right now — and defaulting it here
   * means a server that predates rewards, or one that failed to work them out,
   * produces a menu with no offer rather than a menu that will not draw.
   */
  return { record: body.state, pending: body.pending ?? { daily: 0, weekly: 0 } };
}

/**
 * Collect whatever is waiting, and say what actually landed.
 *
 * Not always what was offered: the menu shows what was pending when it drew, and
 * another client may have collected it since. The server answers with the rows
 * that actually inserted, so this is what to announce.
 */
export async function claimRewards(
  token: string,
): Promise<{ record: PlayerRecord; granted: PendingRewards }> {
  const response = await call('/rewards/claim', token, {
    method: 'POST',
    body: JSON.stringify({}),
  });

  const body = (await response.json()) as { state: PlayerRecord; granted?: PendingRewards };
  return { record: body.state, granted: body.granted ?? { daily: 0, weekly: 0 } };
}

/**
 * Write the record back.
 *
 * Temporary, and the server says so at greater length. Step 3 of the persistence
 * plan replaces this with endpoints that each re-derive their own prices, so
 * that the server rather than the browser decides what a thing costs.
 */
export async function pushAccount(token: string, record: PlayerRecord): Promise<void> {
  await call('/state', token, { method: 'PUT', body: JSON.stringify({ state: record }) });
}

// ── The cache ──────────────────────────────────────────────────────────────

const cacheKey = (uuid: string) => `interspacial:account:${uuid}`;

/** Keep a copy, so a reload has something to draw before the server answers. */
export function cacheAccount(record: PlayerRecord): void {
  try {
    window.localStorage.setItem(cacheKey(record.uuid), JSON.stringify(record));
  } catch {
    // Private browsing, or a full quota. Costs a blank moment on the next load
    // and nothing else — the record itself is safe on the server.
  }
}

export function cachedAccount(uuid: string): PlayerRecord | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(uuid));
    return raw ? (JSON.parse(raw) as PlayerRecord) : null;
  } catch {
    return null;
  }
}

// ── Actions ────────────────────────────────────────────────────────────────

/** An action the server declined. Carries a sentence meant for the player. */
export class RefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefusedError';
  }
}

/**
 * Ask the server to do something, and take its answer as the truth.
 *
 * Every action replies with the whole record, and the caller replaces its state
 * with it rather than applying its own guess. That is what removes the category
 * of bug where something succeeded and the two sides disagree about what it did
 * — and it is what lets the server refuse without the client unwinding anything,
 * because the client never wound anything forward in the first place.
 *
 * The prices shown before the click are still computed here, out of the same
 * shared functions the server decides with. The browser is asking, not telling.
 */
export async function runAction(
  token: string,
  path: string,
  body: Record<string, unknown>,
  /** Guards the one retry below. Not for callers. */
  retried = false,
): Promise<PlayerRecord> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new OfflineError(error);
  }

  if (response.status === 401) throw new NotSignedInError();

  /*
   * Rate limited — asked again rather than reported.
   *
   * ── Why retrying a mutation is safe *here* specifically ────────────────────
   *
   * Retrying a write is normally a good way to do it twice. This one cannot,
   * because of where the refusal comes from: the limiter runs as middleware
   * *before* the route, so a 429 means the handler never ran, no transaction was
   * opened and nothing was read or written. The request did not happen, rather
   * than might have happened — so asking again is asking for the first time.
   *
   * That property is the whole reason this can be papered over instead of shown.
   * A 429 raised from anywhere inside the handler would not have it, and this
   * would have to become a visible refusal instead.
   *
   * Once only, and on the server's own clock. A client that retried until it
   * succeeded would be the runaway loop the limiter exists to stop, politely.
   */
  if (response.status === 429 && !retried) {
    await waitOutLimit(response);
    return runAction(token, path, body, true);
  }

  if (response.status === 409 || response.status === 429) {
    // A refusal, not a failure. "You cannot afford that" is something to show
    // the player where a dropped connection is something to log — and a 429 that
    // survived its retry is the same kind of thing: true, temporary, and the
    // player's to know about rather than a broken connection to report.
    const said = (await response.json().catch(() => ({}))) as { error?: string };
    throw new RefusedError(said.error ?? 'That is not possible right now.');
  }

  if (!response.ok) throw new Error(`${path} failed: ${response.status}`);

  const body_ = (await response.json()) as { state: PlayerRecord };
  return body_.state;
}

/**
 * Say a feature has been used, and find out whether that was the first time.
 *
 * The answer is the server's — see `seeLesson` on the other side. This browser's
 * copy of the record is a picture, and two tabs would each believe they were
 * first.
 */
export async function seeLesson(
  token: string,
  id: string,
): Promise<{ record: PlayerRecord; firstTime: boolean }> {
  const response = await call('/lesson', token, {
    method: 'POST',
    body: JSON.stringify({ id }),
  });

  const body = (await response.json()) as { state: PlayerRecord; firstTime?: boolean };
  return { record: body.state, firstTime: body.firstTime === true };
}

/** Turn the loud half of lessons on or off. The console keeps its one-liners. */
export async function setTips(token: string, on: boolean): Promise<PlayerRecord> {
  const response = await call('/tips', token, {
    method: 'POST',
    body: JSON.stringify({ on }),
  });

  const body = (await response.json()) as { state: PlayerRecord };
  return body.state;
}
